import type { NetworkCode } from '@/api/networkApps';
import type { PlatformInfo, Project } from '@/api/types';

// utils.apps.store code → 라벨. 알 수 없는 code는 uppercase로 폴백.
export function platformLabel(code: string): string {
  const lower = (code || '').toLowerCase();
  if (lower === 'aos') return 'AOS';
  if (lower === 'ios') return 'iOS';
  if (lower === 'web') return 'Web';
  if (lower === 'steam') return 'Steam';
  return (code || '').toUpperCase();
}

// UA 진행 상태 select 옵션 (orbit과 동일).
export const UA_STATE_OPTIONS = [
  { value: '1', label: 'UA 진행 중' },
  { value: '0', label: 'UA 진행 중단' },
];

// 공통 정보 — 모든 OS 공통. (bundle_id는 플랫폼별로 달라 '플랫폼별 정보'로 이동)
export interface CommonState {
  display_name: string;
  app_match_name: string;
  ga_game_id: string;
  tapjoy_app_id: string;
  adjust_app_token: string;
  gcp_project_id: string;
  publisher: string;
  company: string;
}

// 한 platform의 편집 가능한 필드 셋. PlatformInfo의 모든 키를 string으로 노출.
export type PlatformState = {
  sub_project_id: string;
  store_id: string;
  store_url: string;
  bundle_id: string;
  sku: string;
  ga_game_id: string;
  meta_app_id: string;
  tapjoy_app_id: string;
  dt_app_id: string;
  unity_game_id: string;
  vungle_app_id: string;
  state_code: string;
};

export const PLATFORM_KEYS: (keyof PlatformState)[] = [
  'sub_project_id',
  'store_id',
  'store_url',
  'bundle_id',
  'sku',
  'ga_game_id',
  'meta_app_id',
  'tapjoy_app_id',
  'dt_app_id',
  'unity_game_id',
  'vungle_app_id',
  'state_code',
];

// 추가 가능한 platform OS 코드 (사용자 결정 — Android / iOS만). 이미 있는 코드는 메뉴에서 제외.
export const KNOWN_OS_CODES = ['aos', 'ios'];

export function buildCommon(p: Project): CommonState {
  return {
    display_name: p.common.display_name ?? '',
    app_match_name: p.common.app_match_name ?? '',
    ga_game_id: p.common.ga_game_id ?? '',
    tapjoy_app_id: p.common.tapjoy_app_id ?? '',
    adjust_app_token: p.common.adjust_app_token ?? '',
    gcp_project_id: p.common.gcp_project_id ?? '',
    publisher: p.common.publisher ?? '',
    company: p.common.company ?? '',
  };
}

export function buildPlatformValues(info: PlatformInfo): PlatformState {
  return {
    sub_project_id: info.sub_project_id ?? '',
    store_id: info.store_id ?? '',
    store_url: info.store_url ?? '',
    bundle_id: info.bundle_id ?? '',
    sku: info.sku ?? '',
    ga_game_id: info.ga_game_id ?? '',
    meta_app_id: info.meta_app_id ?? '',
    tapjoy_app_id: info.tapjoy_app_id ?? '',
    dt_app_id: info.dt_app_id ?? '',
    unity_game_id: info.unity_game_id ?? '',
    vungle_app_id: info.vungle_app_id ?? '',
    state_code: info.state_code ?? '',
  };
}

// 추가 중 platform의 빈 초기 state. state_code는 '1'(활성) 기본.
export function emptyPlatformState(): PlatformState {
  return {
    sub_project_id: '',
    store_id: '',
    store_url: '',
    bundle_id: '',
    sku: '',
    ga_game_id: '',
    meta_app_id: '',
    tapjoy_app_id: '',
    dt_app_id: '',
    unity_game_id: '',
    vungle_app_id: '',
    state_code: '1',
  };
}

export function buildPlatforms(p: Project): Record<string, PlatformState> {
  const out: Record<string, PlatformState> = {};
  for (const info of p.platforms ?? []) {
    out[info.code] = buildPlatformValues(info);
  }
  return out;
}

// platform별로 어느 필드를 노출할지. orbit은 AOS(store_id, state_code), iOS(store_id, sku, state_code)로 다름.
// 그 외 platform은 PlatformInfo의 모든 키 자동 렌더.
export function fieldsFor(code: string): (keyof PlatformState)[] {
  const lower = (code || '').toLowerCase();
  if (lower === 'aos')
    return ['sub_project_id', 'store_id', 'store_url', 'bundle_id', 'ga_game_id', 'meta_app_id', 'tapjoy_app_id', 'dt_app_id', 'unity_game_id', 'vungle_app_id', 'state_code'];
  if (lower === 'ios')
    return ['sub_project_id', 'store_id', 'store_url', 'bundle_id', 'sku', 'ga_game_id', 'meta_app_id', 'tapjoy_app_id', 'dt_app_id', 'unity_game_id', 'vungle_app_id', 'state_code'];
  return PLATFORM_KEYS;
}

// 모든 플랫폼에 공통으로 존재하는 컬럼 (표시 순서). 그 외(예: sku=iOS 전용)는 '플랫폼 전용'.
// bundle_id는 플랫폼별로 값이 다르지만 모든 플랫폼에 존재하므로 여기 포함(공통 항목 그룹).
const COMMON_PLATFORM_FIELDS: (keyof PlatformState)[] = [
  'sub_project_id',
  'store_id',
  'store_url',
  'bundle_id',
  'ga_game_id',
  'meta_app_id',
  'tapjoy_app_id',
  'dt_app_id',
  'unity_game_id',
  'vungle_app_id',
  'state_code',
];

export interface PlatformFieldGroup {
  label: string;
  fields: (keyof PlatformState)[];
}

// '플랫폼별 정보' 카드 안에서 필드를 '공통 항목'(모든 플랫폼 공통) / '플랫폼 전용'(특정 플랫폼만)으로 나눈다.
export function fieldGroupsFor(code: string): PlatformFieldGroup[] {
  const all = fieldsFor(code);
  const common = COMMON_PLATFORM_FIELDS.filter((k) => all.includes(k));
  const exclusive = all.filter((k) => !COMMON_PLATFORM_FIELDS.includes(k));
  const groups: PlatformFieldGroup[] = [{ label: '공통 항목', fields: common }];
  if (exclusive.length > 0) groups.push({ label: '플랫폼 전용', fields: exclusive });
  return groups;
}

// 필드별 도움말.
export const FIELD_HELP: Record<string, string> = {
  sub_project_id: '시스템 자동 발급 (utils.apps.project_sub_id)',
  store_id: '스토어 식별자 (Google Play 패키지명 / App Store ID)',
  store_url: '스토어 페이지 URL — 변경 요청을 관리자 승인 후 반영합니다',
  bundle_id: '플랫폼별 번들 ID',
  sku: 'iTunes Connect SKU',
  ga_game_id: 'GameAnalytics 게임 ID (플랫폼별로 다름)',
  meta_app_id: 'Meta(Facebook) 앱 ID — 교체 시 비즈니스 연결 여부는 미확인으로 초기화됩니다. App Secret은 PSL에서 별도로 관리합니다',
  tapjoy_app_id: 'Tapjoy 앱 ID (플랫폼별로 다름)',
  dt_app_id: 'DT FairBid 앱 ID — 조회 버튼으로 store_id에 맞는 값을 채웁니다',
  unity_game_id: 'Unity Game ID — 조회 버튼으로 store_id에 맞는 값을 채웁니다',
  vungle_app_id: 'Vungle(Liftoff) 앱 ID — 조회 버튼으로 store_id에 맞는 값을 채웁니다',
  state_code: 'UA 진행 상태 (진행 중 / 진행 중단)',
};

// 필드별 required 여부. 모든 플랫폼의 store_id는 필수, bundle_id는 aos/ios만 필수, iOS sku는 필수.
export function isRequiredField(platformCode: string, key: keyof PlatformState): boolean {
  const lower = platformCode.toLowerCase();
  if (key === 'store_id') return true;
  if (key === 'bundle_id') return lower === 'aos' || lower === 'ios';
  return lower === 'ios' && key === 'sku';
}

// sub_project_id는 시스템 자동 발급 — readonly. 그 외는 누구나 수정 요청할 수 있다.
export function isReadonlyField(key: keyof PlatformState): boolean {
  return key === 'sub_project_id';
}

// 네트워크 Management API 조회로 채우는 필드. 네트워크가 늘면 여기에 한 줄 추가하면
// 조회 버튼과 필드 잠금이 함께 따라온다 (backend SUPPORTED_NETWORKS와 짝).
export interface NetworkFieldSpec {
  network: NetworkCode;
  label: string;                 // 버튼에 쓰는 짧은 이름
  field: keyof PlatformState;
}

export const NETWORK_FIELD_SPECS: NetworkFieldSpec[] = [
  { network: 'dt', label: 'DT', field: 'dt_app_id' },
  { network: 'unity', label: 'Unity', field: 'unity_game_id' },
  { network: 'vungle', label: 'Vungle', field: 'vungle_app_id' },
  { network: 'tapjoy', label: 'Tapjoy', field: 'tapjoy_app_id' },
];

export function networkSpecForField(key: keyof PlatformState): NetworkFieldSpec | undefined {
  return NETWORK_FIELD_SPECS.find((spec) => spec.field === key);
}

// 변경 내역(diff) 한 항목 — scope는 '공통' 또는 platform 라벨.
export interface DiffItem {
  scope: string;
  field: string;
  before: string;
  after: string;
}
